/**
 * Модульный кэш workflow-runtime'ов. Заполняется ``useWorkflowPolicy`` и
 * `prefetchWorkflowPolicy`; синхронные читатели используются в местах,
 * где рефакторинг на хук неуместен (print-мапперы, Search-лейблы и т.п.).
 *
 * Кэш также персистится в ``localStorage`` для мгновенного ответа при
 * повторных загрузках страницы — сам `useWorkflowPolicy` обновляет данные
 * в фоне (SWR).
 */

import { getWorkflowRuntime } from '../api/systemConsole';

// Bumped to ``:v2`` вместе с переходом ``roles`` → ``scopes`` и появлением
// ``profile_types``/``permissions`` в runtime-ответе. Старые снимки из
// localStorage по ключу ``workflow-runtime:*`` не читаются — при первом
// заходе после обновления UI делает сетевой запрос и пишет в новый ключ.
const LS_PREFIX = 'workflow-runtime:v2:';
const _LEGACY_LS_PREFIXES = ['workflow-runtime:'];

function _purgeLegacyLocalStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const toRm = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      for (const legacy of _LEGACY_LS_PREFIXES) {
        if (key.startsWith(legacy) && !key.startsWith(LS_PREFIX)) {
          toRm.push(key);
          break;
        }
      }
    }
    toRm.forEach((k) => window.localStorage.removeItem(k));
  } catch (_e) {
    /* noop */
  }
}

_purgeLegacyLocalStorage();

/**
 * Коды типов документов, для которых workflow-runtime прогревается на
 * старте приложения. Используется в `prefetchAllWorkflowPolicies` из
 * AuthProvider после успешной аутентификации. Если в системе появится
 * новый тип документа — его нужно добавить сюда, чтобы UI/print-мапперы
 * не делали lazy-fetch при первом рендере.
 */
export const KNOWN_DOCUMENT_TYPES = Object.freeze([
  'COMING',
  'CUSTOMER_ORDER',
  'DEPOT_RETURN',
  'INVENTORY',
  'MOVE',
  'ORDER_GARANT',
  'ORDER_NOT_GARANT',
  'REPAIR',
  'SALE',
  'SWAP',
  'WRITEOFF',
]);

/** @type {Map<string, any>} */
const _byType = new Map();
const _pending = new Map();
const _listeners = new Set();

/** Добавить подписчика на обновления любого типа документа. */
export function subscribeWorkflow(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function _notify(type, data) {
  for (const fn of _listeners) {
    try {
      fn(type, data);
    } catch (_e) {
      /* listener errors не должны рушить цикл */
    }
  }
}

/** Положить runtime в кэш (оба уровня: RAM + localStorage). */
export function cacheWorkflowPolicy(type, runtimeData) {
  if (!type || !runtimeData) return;
  _byType.set(type, runtimeData);
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        `${LS_PREFIX}${type}`,
        JSON.stringify({ v: runtimeData.version ?? 0, data: runtimeData }),
      );
    }
  } catch (_e) {
    /* localStorage may be full/disabled; не критично */
  }
  _notify(type, runtimeData);
}

/** Синхронно вернуть runtime из RAM-кэша или подтянуть из localStorage (один раз). */
export function getCachedWorkflow(type) {
  if (!type) return null;
  if (_byType.has(type)) return _byType.get(type);
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(`${LS_PREFIX}${type}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.data) {
          _byType.set(type, parsed.data);
          return parsed.data;
        }
      }
    }
  } catch (_e) {
    /* noop */
  }
  return null;
}

/** Прогреть кэш сетевым запросом. Дедуплицируется параллельно. */
export async function prefetchWorkflowPolicy(type) {
  if (!type) return null;
  if (_pending.has(type)) return _pending.get(type);
  const p = getWorkflowRuntime(type)
    .then((data) => {
      cacheWorkflowPolicy(type, data);
      return data;
    })
    .finally(() => {
      _pending.delete(type);
    });
  _pending.set(type, p);
  return p;
}

/**
 * Прогреть кэш сетевыми запросами для всех известных типов документов
 * параллельно. Вызывается при старте приложения (после аутентификации),
 * чтобы synchronous-читатели (`getStatusLabel`, `getStatusRow`,
 * `isEditAllowedSync`, print-мапперы) получали готовые данные без
 * fallback на legacy-конфигурацию.
 *
 * Возвращает Promise, который резолвится после того, как все запросы
 * завершились (как успехом, так и ошибкой — ошибки проглатываются, чтобы
 * один проблемный тип не блокировал остальные).
 */
export async function prefetchAllWorkflowPolicies() {
  const results = await Promise.allSettled(
    KNOWN_DOCUMENT_TYPES.map((type) => prefetchWorkflowPolicy(type)),
  );
  return results.map((r, idx) => ({
    type: KNOWN_DOCUMENT_TYPES[idx],
    status: r.status,
    reason: r.status === 'rejected' ? r.reason : undefined,
  }));
}

/** Инвалидировать кэш (например, после сохранения в админке). */
export function invalidateWorkflowPolicy(type) {
  if (!type) {
    _byType.clear();
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const toRm = [];
        for (let i = 0; i < window.localStorage.length; i += 1) {
          const key = window.localStorage.key(i);
          if (!key) continue;
          if (
            key.startsWith(LS_PREFIX) ||
            _LEGACY_LS_PREFIXES.some((p) => key.startsWith(p))
          ) {
            toRm.push(key);
          }
        }
        toRm.forEach((k) => window.localStorage.removeItem(k));
      }
    } catch (_e) {
      /* noop */
    }
    return;
  }
  _byType.delete(type);
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(`${LS_PREFIX}${type}`);
      for (const legacy of _LEGACY_LS_PREFIXES) {
        window.localStorage.removeItem(`${legacy}${type}`);
      }
    }
  } catch (_e) {
    /* noop */
  }
}

/**
 * Синхронный доступ к label статуса для consumer'ов, которым не подходит
 * асинхронный хук (Search, print-мапперы). Если кэш пуст — возвращает код
 * и инициирует фоновый prefetch, чтобы следующий рендер увидел данные.
 */
export function getStatusLabel(type, statusCode) {
  const data = getCachedWorkflow(type);
  if (!data) {
    prefetchWorkflowPolicy(type).catch(() => {});
    return statusCode;
  }
  const row = data.statuses.find((s) => s.code === statusCode);
  return row?.label_ru || statusCode;
}

/** Синхронный доступ к row статуса (для Search, где нужны `color`/`icon_code`). */
export function getStatusRow(type, statusCode) {
  const data = getCachedWorkflow(type);
  if (!data) {
    prefetchWorkflowPolicy(type).catch(() => {});
    return null;
  }
  return data.statuses.find((s) => s.code === statusCode) || null;
}

/**
 * Sync-аналог legacy ``isEditAllowed(documentType, status)``. Пока runtime
 * не закэширован — возвращает ``true`` (оптимистичный default), чтобы UI
 * не блокировал редактирование на первом рендере. После подгрузки runtime
 * значение станет корректным и консьюмеры перерисуются через обычный
 * react state (значение читается заново при следующем рендере).
 */
export function isEditAllowedSync(type, statusCode) {
  const data = getCachedWorkflow(type);
  if (!data) {
    prefetchWorkflowPolicy(type).catch(() => {});
    return true;
  }
  const row = data.statuses.find((s) => s.code === statusCode);
  if (!row) return false;
  return Boolean(row.allow_edit);
}
